import React from 'react';
import { Box, Grid, Typography } from '@mui/material';

import { useEventTracking } from '../../../services/statsig';
import type { BillingInterval, BundleWithStatus } from '../types';

import { BillingIntervalToggle } from './BillingIntervalToggle';
import { TierCard } from './TierCard';

interface TierSelectionStepProps {
  tiers: BundleWithStatus[];
  selectedTierId: string | null;
  billingInterval: BillingInterval;
  onSelectTier: (tierId: string) => void;
  onBillingIntervalChange: (interval: BillingInterval) => void;
  onNext: () => void;
  discountRange?: { min: number; max: number } | null;
}

export const TierSelectionStep: React.FC<TierSelectionStepProps> = ({
  tiers,
  selectedTierId,
  billingInterval,
  onSelectTier,
  onBillingIntervalChange,
  onNext,
  discountRange,
}) => {
  const { subscription } = useEventTracking();

  // Determine recommended tier (typically the middle one)
  const recommendedTierIndex = Math.floor(tiers.length / 2);

  // Auto-advance to next step when tier is selected
  // Skip add-ons step if "Everything" tier is selected (includes all add-ons)
  const handleSelectTier = (tierId: string) => {
    const selectedTier = tiers.find(t => t.bundle_id === tierId);
    const previousTier = selectedTierId ? tiers.find(t => t.bundle_id === selectedTierId) : null;

    if (selectedTier) {
      // Track tier changed if switching from a different tier
      if (previousTier && previousTier.bundle_id !== selectedTier.bundle_id) {
        subscription.tierChanged({
          from_tier_id: previousTier.bundle_id,
          to_tier_id: selectedTier.bundle_id,
          from_tier_name: previousTier.name,
          to_tier_name: selectedTier.name,
        });
      }

      // Track tier selected (outcome)
      subscription.tierSelected({
        tier_id: selectedTier.bundle_id,
        tier_name: selectedTier.name,
        billing_interval: billingInterval,
        monthly_price: selectedTier.monthly_unit_amount,
        yearly_price: selectedTier.yearly_unit_amount,
      });

      // Track continue click
      subscription.tierContinueClicked({
        tier_id: selectedTier.bundle_id,
        tier_name: selectedTier.name,
        billing_interval: billingInterval,
      });
    }

    onSelectTier(tierId);

    // Check if this is the "Everything" tier which includes all add-ons
    const isEverythingTier = tierId === 'tier-everything';

    if (isEverythingTier) {
      // Skip add-ons step by advancing twice (to Confirm & Pay)
      onNext();
      setTimeout(() => onNext(), 0);
    } else {
      // Go to add-ons step
      onNext();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Build your Plan
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
          Flexible options for your practice&apos;s every need
        </Typography>

        {/* Billing Interval Toggle */}
        <BillingIntervalToggle
          value={billingInterval}
          onChange={onBillingIntervalChange}
          discountRange={discountRange}
        />
      </Box>

      {/* Tier Cards Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {tiers.map((tier, index) => (
          <Grid item xs={12} md={4} key={tier.bundle_id}>
            <TierCard
              bundle={tier}
              billingInterval={billingInterval}
              isSelected={selectedTierId === tier.bundle_id}
              isRecommended={index === recommendedTierIndex}
              onSelect={handleSelectTier}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
